import { v4 as uuidv4 } from 'uuid'
import { UserJourney } from './types'

export type JourneyMarker = {
  journey: UserJourney
  esid: string
  pageLoad?: string
  handoff?: boolean
  checkoutToken?: string
}

type JourneyMarkerOptions = {
  checkoutToken?: string
  handoff?: boolean
  replaceCheckoutToken?: boolean
}

const ACTIVE_JOURNEY_MARKER_KEY = 'apt-uj-active-journey'

let pageLoadId = uuidv4()

export const clearJourneyMarker = (): void => {
  try {
    window.sessionStorage.removeItem(ACTIVE_JOURNEY_MARKER_KEY)
  } catch {
    return
  }
}

export const readJourneyMarker = (): JourneyMarker | undefined => {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_JOURNEY_MARKER_KEY)
    if (raw === null) {
      return undefined
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined
    }
    const { journey, esid, handoff, pageLoad, checkoutToken } = parsed as {
      journey?: Partial<UserJourney>
      esid?: unknown
      handoff?: unknown
      pageLoad?: unknown
      checkoutToken?: unknown
    }
    if (typeof esid !== 'string' || typeof journey !== 'object' || journey === null) {
      return undefined
    }
    if (typeof journey.id !== 'string' || typeof journey.name !== 'string' || typeof journey.created !== 'number') {
      return undefined
    }
    return {
      esid,
      ...(typeof pageLoad === 'string' ? { pageLoad } : {}),
      ...(handoff === true ? { handoff: true } : {}),
      ...(typeof checkoutToken === 'string' ? { checkoutToken } : {}),
      journey: {
        id: journey.id,
        name: journey.name,
        created: journey.created,
        variant: typeof journey.variant === 'string' ? journey.variant : undefined,
        frustrations: Array.isArray(journey.frustrations) ? journey.frustrations : [],
        frictions: Array.isArray(journey.frictions) ? journey.frictions : [],
        tags: Array.isArray(journey.tags) ? journey.tags : [],
      },
    }
  } catch {
    return undefined
  }
}

export const writeJourneyMarker = (journey: UserJourney, esid: string, options: JourneyMarkerOptions = {}): void => {
  try {
    const existingMarker = readJourneyMarker()
    const requestedCheckoutToken = options.checkoutToken === '' ? undefined : options.checkoutToken
    const checkoutToken =
      options.replaceCheckoutToken === true
        ? requestedCheckoutToken
        : existingMarker?.journey.id === journey.id && existingMarker.checkoutToken !== undefined && existingMarker.checkoutToken !== ''
        ? existingMarker.checkoutToken
        : requestedCheckoutToken
    window.sessionStorage.setItem(
      ACTIVE_JOURNEY_MARKER_KEY,
      JSON.stringify({
        journey,
        esid,
        pageLoad: pageLoadId,
        ...(checkoutToken !== undefined ? { checkoutToken } : {}),
        ...(options.handoff === true ? { handoff: true } : {}),
      })
    )
  } catch {
    return
  }
}

export const markJourneyMarkerHandoff = (checkoutToken: string): void => {
  try {
    const marker = readJourneyMarker()
    if (marker?.checkoutToken === checkoutToken) {
      window.sessionStorage.setItem(ACTIVE_JOURNEY_MARKER_KEY, JSON.stringify({ ...marker, handoff: true }))
    }
  } catch {
    return
  }
}

export const isMarkerFromCurrentPageLoad = (marker: JourneyMarker): boolean => marker.pageLoad === pageLoadId

export const resetPageLoadForTesting = (): void => {
  pageLoadId = uuidv4()
}
